import { useEffect, useRef, useState } from "react";
const DEFAULT_DEBOUNCE_MS = 400;
// Reusable debounced + cancellable fetch. First of its kind in this app (no prior
// AbortController usage anywhere) — built for the Objects smart search (item #6)
// but written generically so the Contacts section (item #7) can reuse it as-is.
export function useDebouncedFetch({ fetcher, deps, isActive, debounceMs = DEFAULT_DEBOUNCE_MS, }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const timerRef = useRef(undefined);
    useEffect(() => {
        var _a;
        if (timerRef.current)
            window.clearTimeout(timerRef.current);
        if (!isActive) {
            (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
            setData(null);
            setLoading(false);
            setError(null);
            return;
        }
        timerRef.current = window.setTimeout(() => {
            var _a;
            (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setLoading(true);
            setError(null);
            fetcher({ signal: controller.signal })
                .then((res) => {
                setData(res.data);
                setLoading(false);
            })
                .catch((err) => {
                const isCancelled = (err === null || err === void 0 ? void 0 : err.code) === "ERR_CANCELED" || (err === null || err === void 0 ? void 0 : err.name) === "CanceledError";
                if (isCancelled)
                    return;
                setError(err);
                setLoading(false);
            });
        }, debounceMs);
        return () => {
            if (timerRef.current)
                window.clearTimeout(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    useEffect(() => {
        return () => {
            var _a;
            (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        };
    }, []);
    return { data, loading, error };
}
